import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
    GetBatterys,
    GetTariffs,
    GetInstallationLocations,
    APIGetEVChargers,
    APIGetAllInverters,
    APIGetAllACControllers,
    APIAncillaryCosts
} from '../../Api'

// Async thunk to load all catalog data
export const loadCatalogData = createAsyncThunk(
    'catalog/loadAll',
    async (_, { rejectWithValue }) => {
        try {
            const [
                batteries,
                tariffs,
                installationLocations,
                evChargers,
                inverters,
                acControllers,
                ancillaryCosts
            ] = await Promise.all([
                GetBatterys(),
                GetTariffs(),
                GetInstallationLocations(),
                APIGetEVChargers(),
                APIGetAllInverters(),
                APIGetAllACControllers(),
                APIAncillaryCosts()
            ])

            return {
                batteries: batteries || [],
                tariffs: tariffs || [],
                installationLocations: installationLocations || [],
                evChargers: evChargers || [],
                inverters: inverters || [],
                acControllers: acControllers || [],
                ancillaryCosts: ancillaryCosts || []
            }
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to load catalog data')
        }
    }
)

const initialState = {
    batteries: [],
    tariffs: [],
    installationLocations: [],
    evChargers: [],
    inverters: [],
    acControllers: [],
    ancillaryCosts: [],
    financeRates: [],
    maintenanceOptions: [],
    panels: [],
    optimisers: [],
    loading: false,
    loaded: false,
    error: null
}

const catalogSlice = createSlice({
    name: 'catalog',
    initialState,
    reducers: {
        setBatteries: (state, action) => {
            state.batteries = action.payload
        },
        setTariffs: (state, action) => {
            state.tariffs = action.payload
        },
        setInstallationLocations: (state, action) => {
            state.installationLocations = action.payload
        },
        setEVChargers: (state, action) => {
            state.evChargers = action.payload
        },
        setInverters: (state, action) => {
            state.inverters = action.payload
        },
        setACControllers: (state, action) => {
            state.acControllers = action.payload
        },
        setAncillaryCosts: (state, action) => {
            state.ancillaryCosts = action.payload
        },
        setFinanceRates: (state, action) => {
            state.financeRates = action.payload
        },
        setMaintenanceOptions: (state, action) => {
            state.maintenanceOptions = action.payload
        },
        setPanels: (state, action) => {
            state.panels = action.payload
        },
        setOptimisers: (state, action) => {
            state.optimisers = action.payload
        },
        clearError: (state) => {
            state.error = null
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadCatalogData.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(loadCatalogData.fulfilled, (state, action) => {
                state.loading = false
                state.loaded = true
                state.batteries = action.payload.batteries
                state.tariffs = action.payload.tariffs
                state.installationLocations = action.payload.installationLocations
                state.evChargers = action.payload.evChargers
                state.inverters = action.payload.inverters
                state.acControllers = action.payload.acControllers
                state.ancillaryCosts = action.payload.ancillaryCosts
            })
            .addCase(loadCatalogData.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
    }
})

export const {
    setBatteries,
    setTariffs,
    setInstallationLocations,
    setEVChargers,
    setInverters,
    setACControllers,
    setAncillaryCosts,
    setFinanceRates,
    setMaintenanceOptions,
    setPanels,
    setOptimisers,
    clearError
} = catalogSlice.actions

// Selectors
export const selectBatteries = (state) => state.catalog.batteries
export const selectTariffs = (state) => state.catalog.tariffs
export const selectInstallationLocations = (state) => state.catalog.installationLocations
export const selectEVChargers = (state) => state.catalog.evChargers
export const selectInverters = (state) => state.catalog.inverters
export const selectACControllers = (state) => state.catalog.acControllers
export const selectAncillaryCosts = (state) => state.catalog.ancillaryCosts
export const selectCatalogLoading = (state) => state.catalog.loading
export const selectCatalogLoaded = (state) => state.catalog.loaded
export const selectCatalogError = (state) => state.catalog.error
export const selectPanels = (state) => state.catalog.panels
export const selectOptimisers = (state) => state.catalog.optimisers

export default catalogSlice.reducer
